// src/features/records/components/MapView/MapView.js
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MapView.module.css';
import MapCanvas from './components/MapCanvas';
import MapControls from './components/MapControls';
import LoadingOverlay from './components/LoadingOverlay';
import LoadingDots from './components/LoadingDots';
import MapSkeleton from './components/MapSkeleton';
import RecordErrorBoundary from '../RecordErrorBoundary';
import useRecordDetail from '../../hooks/useRecordDetail';
import useViewportRecords from './hooks/useViewportRecords';
import useFilterStore from '../../stores/useFilterStore';
import useGeolocation from './hooks/useGeolocation';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import { useToast } from '../../../../hooks/useToast';
import { calculateDistance } from './utils/geohash';
import { logError, ErrorSeverity, ErrorCategory } from '../../../../services/errorService';
import { useRecordsLayoutContext } from '../../hooks/useRecordsLayoutContext';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';

// Distance threshold in meters to consider map centered on user location
// 100m is roughly a city block - close enough to be considered "at" the location
const LOCATION_CENTERED_THRESHOLD_M = 100;

// Animation duration constants (in milliseconds)
const ANIMATION_DURATIONS = {
  clusterFlyToBase: 600,      // Base duration for cluster zoom animation
  perZoomStep: 100,          // Additional duration per zoom level difference
  maxDuration: 1200,         // Maximum animation duration
  geoLocationBase: 400,      // Base duration for geolocation fly-to
  geoLocationPerZoom: 50,    // Additional duration per zoom level
  geoLocationMax: 800        // Maximum geolocation animation duration
};

const MapView = () => {
  const { t } = useTranslation();
  const { searchQuery } = useRecordsLayoutContext();
  const toast = useToast();
  const mapRef = useRef(null);
  const { openDetail } = useRecordDetail();
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [mapBounds, setMapBounds] = useState(null);
  const scope = useFilterStore((state) => state.scope);
  
  // Lock body scroll for canvas view
  useBodyScrollLock(true);
  
  // Get user's location
  const { location: userLocation, loading: locationLoading } = useGeolocation();

  // Load records for current viewport
  const {
    records,
    loading: recordsLoading,
    error: recordsError
  } = useViewportRecords(mapBounds, {
    searchTerm: searchQuery,
    scope
  });
  const mapErrorKey = recordsError
    ? mapRecordsErrorToKey(recordsError, { fallbackKey: 'records.views.map.errors.load_failed' })
    : null;

  // Compute if map is centered on user location
  const isLocationCentered = useMemo(() => {
    if (!userLocation || !mapBounds?.center) return false;
    
    // Calculate spherical distance in kilometers
    const distanceKm = calculateDistance(
      mapBounds.center.lat,
      mapBounds.center.lng,
      userLocation.latitude,
      userLocation.longitude
    );
    
    // Convert to meters and check against threshold
    return (distanceKm * 1000) < LOCATION_CENTERED_THRESHOLD_M;
  }, [userLocation, mapBounds]);

  // Keep records in a ref for stable callbacks
  const recordsRef = useRef([]);
  useEffect(() => {
    recordsRef.current = records;
  }, [records]);

  // Convert records to GeoJSON for Mapbox
  const geoJsonData = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: records
        .filter((record) => {
          const lat = record?.attributes?.location?.lat;
          const lng = record?.attributes?.location?.lng;
          return Number.isFinite(lat) && Number.isFinite(lng);
        })
        .map((record) => {
          const lat = record.attributes.location.lat;
          const lng = record.attributes.location.lng;
          const title = record.content?.title || t('records.views.map.item.untitled');

          return {
            type: 'Feature',
            id: record.id,
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            },
            properties: {
              id: record.id,
              title,
              // Truncate title to 30 characters (fits comfortably in 200px pin width)
              displayTitle: title.slice(0, 30) + ((title || '').length > 30 ? '...' : '')
            }
          };
        })
    };
  }, [records, t]);

  // Handle map viewport changes
  const handleViewportChange = useCallback((bounds) => {
    setMapBounds(bounds);
  }, []);

  // Handle pin selection
  const handlePinClick = useCallback((recordId) => {
    setSelectedPinId(recordId);
    const record = recordsRef.current.find(r => r.id === recordId);
    if (record) {
      openDetail(record);
    }
  }, [openDetail]);

  // Handle cluster click
  const handleClusterClick = useCallback((e) => {
    if (!mapRef.current) return;

    // Extract data synchronously before async operations
    const clusterFeature = e.features?.[0];
    if (!clusterFeature) return; // graceful no-op

    const { cluster_id: clusterId } = clusterFeature.properties;
    const coordinates = clusterFeature.geometry.coordinates;

    const mapInstance = mapRef.current.getMap();
    const source = mapInstance.getSource('records');

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      
      // Calculate a dynamic duration based on zoom difference
      const currentZoom = mapInstance.getZoom();
      const maxZoom = mapInstance.getMaxZoom();
      const targetZoom = Math.min(zoom + 0.5, maxZoom); // Clamp to max zoom
      const zoomDiff = Math.abs(targetZoom - currentZoom);
      const duration = Math.min(
        ANIMATION_DURATIONS.clusterFlyToBase + (zoomDiff * ANIMATION_DURATIONS.perZoomStep), 
        ANIMATION_DURATIONS.maxDuration
      );
      
      mapInstance.flyTo({
        center: coordinates,
        zoom: targetZoom,
        duration: duration,
        curve: 1.42, // Smoother spring-like curve
        speed: 1.2,
        easing: (t) => {
          // Ease out cubic - smooth deceleration
          return 1 - Math.pow(1 - t, 3);
        }
      });
    });
  }, []); // Empty dependency array - no external dependencies

  // Handle geolocation control click
  const handleGeolocationClick = useCallback(() => {
    if (userLocation && mapRef.current) {
      const mapInstance = mapRef.current.getMap();
      const currentZoom = mapInstance.getZoom();
      const targetZoom = 14;
      const zoomDiff = Math.abs(targetZoom - currentZoom);
      const duration = Math.min(
        ANIMATION_DURATIONS.geoLocationBase + (zoomDiff * ANIMATION_DURATIONS.geoLocationPerZoom), 
        ANIMATION_DURATIONS.geoLocationMax
      );
      
      mapInstance.flyTo({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: targetZoom,
        duration: duration,
        curve: 1.42,
        speed: 1.5,
        easing: (t) => {
          // Ease out cubic - smooth deceleration
          return 1 - Math.pow(1 - t, 3);
        }
      });
    } else {
      toast.error(t('records.views.map.toasts.location_unavailable'));
    }
  }, [userLocation, toast, t]);

  // Handle map initialization
  const handleMapLoad = useCallback(() => {
    setInitializing(false);
  }, []);

  // Handle errors
  const handleError = useCallback((error) => {
    // Log to analytics
    logError(error, {
      severity: ErrorSeverity.CRITICAL,
      category: ErrorCategory.MAP,
      component: 'MapView',
      action: 'mapOperation',
      metadata: {
        errorType: error.name || 'MapError',
        hasUserLocation: !!userLocation,
        recordCount: records.length
      }
    });

    const errorKey = mapRecordsErrorToKey(error, {
      fallbackKey: 'records.views.map.errors.operation_failed'
    });

    toast.error(t(errorKey));
  }, [toast, userLocation, records.length, t]);

  if (recordsError) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{t(mapErrorKey || 'records.views.map.errors.load_failed')}</p>
        <button 
          onClick={() => window.location.reload()} 
          className={styles.retryButton}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <RecordErrorBoundary>
      <div className={styles.mapViewContainer}>
        {initializing && (
          <>
            <MapSkeleton />
            <LoadingOverlay 
              message={t('records.views.map.loading.initializing')}
              showAfterDelay={500}
            />
          </>
        )}
        
        <MapCanvas
          ref={mapRef}
          geoJsonData={geoJsonData}
          records={records}
          selectedPinId={selectedPinId}
          userLocation={userLocation}
          onViewportChange={handleViewportChange}
          onPinClick={handlePinClick}
          onClusterClick={handleClusterClick}
          onMapLoad={handleMapLoad}
          onError={handleError}
        />
        
        {!initializing && (
          <MapControls
            onGeolocationClick={handleGeolocationClick}
            hasUserLocation={!!userLocation}
            isLocationCentered={isLocationCentered}
            className={styles.controls}
          />
        )}
        
        <LoadingDots isVisible={!initializing && (locationLoading || recordsLoading)} />
      </div>
    </RecordErrorBoundary>
  );
};

export default MapView;
